import { useMemo } from 'react';
import { normalizeServiceList } from '../config/catalog/normalizedServiceSchema';

const normalize = (value) => String(value || '').trim().toLowerCase();

const isLocationAgnostic = (locationText) => {
  const value = normalize(locationText);
  if (!value) return true;

  // Keep services that are intentionally generic and not city-bound.
  return /(home|venue|temple|online|virtual|anywhere|remote)/.test(value);
};

const buildSearchText = (parts = []) => {
  return parts
    .flat()
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

export function useHomeFilters({
  searchTerm,
  selectedCity,
  defaultCityLabel,
  categories,
  popularServices,
  newServices
}) {
  const normalizedSearchTerm = useMemo(() => normalize(searchTerm), [searchTerm]);

  const normalizedCityFilter = useMemo(() => {
    if (!selectedCity || selectedCity === defaultCityLabel) {
      return '';
    }
    return normalize(selectedCity.split(',')[0]);
  }, [selectedCity, defaultCityLabel]);

  const filteredCategories = useMemo(() => {
    if (!normalizedSearchTerm) {
      return categories;
    }

    return categories.filter((category) => {
      const searchable = buildSearchText([
        category.id,
        category.title,
        category.description,
        Array.isArray(category.services) ? category.services : []
      ]);

      return searchable.includes(normalizedSearchTerm);
    });
  }, [categories, normalizedSearchTerm]);

  const filteredPopularServices = useMemo(() => {
    let data = normalizeServiceList(popularServices, { servicePage: 'most-booked-services' });

    if (normalizedCityFilter) {
      data = data.filter((service) => {
        const location = normalize(service.location);
        if (isLocationAgnostic(location)) {
          return true;
        }
        return location.includes(normalizedCityFilter);
      });
    }

    if (!normalizedSearchTerm) {
      return data;
    }

    return data.filter((service) => {
      const searchable = buildSearchText([
        service.name,
        service.title,
        service.description,
        service.category,
        service.tags,
        service.duration,
        service.location,
        Array.isArray(service.features) ? service.features : []
      ]);

      return searchable.includes(normalizedSearchTerm);
    });
  }, [popularServices, normalizedCityFilter, normalizedSearchTerm]);

  const filteredNewServices = useMemo(() => {
    let data = normalizeServiceList(newServices, { servicePage: 'new-services' });

    if (normalizedCityFilter) {
      data = data.filter((service) => {
        const location = normalize(service.location);
        if (isLocationAgnostic(location)) {
          return true;
        }
        return location.includes(normalizedCityFilter);
      });
    }

    if (!normalizedSearchTerm) {
      return data;
    }

    return data.filter((service) => {
      const searchable = buildSearchText([
        service.name,
        service.title,
        service.description,
        service.detailedDescription,
        service.category,
        service.tags,
        service.duration,
        Array.isArray(service.features) ? service.features : []
      ]);

      return searchable.includes(normalizedSearchTerm);
    });
  }, [newServices, normalizedCityFilter, normalizedSearchTerm]);

  return {
    normalizedSearchTerm,
    normalizedCityFilter,
    filteredCategories,
    filteredPopularServices,
    filteredNewServices
  };
}

export default useHomeFilters;
